import { useSessao } from '../../../auth/contexto'
import { Botao } from '../../../components/Botao'
import { AvisoDeErro, Esqueleto } from '../../../components/Estados'
import { SeloDeMaquina } from '../../../components/SeloDeMaquina'
import { SeveridadeDaIa } from '../../../components/SeveridadeDaIa'
import { porSeveridade } from '../../../lib/severidade'
import { TEXTOS } from '../../../lib/glossario'
import {
  useAnalisarGmud,
  usePareceresDaGmud,
  useRevisoesDePr,
  useSituacaoDaIaDeGmud,
  useSituacaoDasRevisoes,
} from '../../../api/queries/gmuds'
import type { ParecerDeGmud, RecomendacaoDaIa, UsuarioLogado } from '../../../types/gmud'
import { CommitsDaGmud } from './CommitsDaGmud'
import { defasado } from './parecerDefasado'
import { RevisoesDeCodigo } from './RevisoesDeCodigo'

/** Os mesmos papéis do `@PreAuthorize` do servidor — conveniência de tela, não regra. */
const PAPEIS_DO_COMITE = ['CAB_MEMBRO', 'CAB_PRESIDENTE', 'CAB_PRESIDENTE_SUPLENTE', 'ADMIN']

/**
 * O apoio à decisão do comitê: a revisão de código dos PRs e o parecer de IA.
 *
 * Fica FORA das abas, junto das ações, porque é para a decisão que ele existe. E o limite está
 * no texto da tela: recomendação não é decisão — o clique de aprovar continua sendo humano,
 * nas ações acima (Regras nº 1 e nº 3).
 */
export function PainelDeApoioDoComite({ gmudId, origem, status }: { gmudId: number; origem: string; status: string }) {
  const { usuario } = useSessao()
  const situacao = useSituacaoDaIaDeGmud()
  const revisoes = useRevisoesDePr(gmudId)
  const situacaoDasRevisoes = useSituacaoDasRevisoes()
  const pareceres = usePareceresDaGmud(gmudId)
  const analisar = useAnalisarGmud(gmudId)

  // Registro migrado é histórico terminal: não há decisão a apoiar.
  if (origem === 'MIGRADO_V1') {
    return null
  }

  const temRevisoes = (revisoes.data?.length ?? 0) > 0
  const iaDisponivel = situacao.data?.disponivel === true
  // Sem IA configurada e sem revisão nenhuma, o cartão viraria aviso permanente: some.
  if (!situacao.isPending && !iaDisponivel && !temRevisoes) {
    return null
  }

  const parecer = pareceres.data?.find((p) => p.situacao !== 'EM_ANDAMENTO') ?? null
  const parecerDefasado = defasado(parecer, revisoes.data ?? [])

  return (
    <section className="cartao space-y-4 p-5" aria-label={TEXTOS.apoioAoComite.titulo}>
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-slate-900">{TEXTOS.apoioAoComite.titulo}</h2>
          <p className="mt-1 max-w-3xl text-sm text-slate-500">{TEXTOS.apoioAoComite.ajuda}</p>
        </div>
        {iaDisponivel && doComite(usuario) && (
          <Botao
            aparencia="secundario"
            tamanho="pequeno"
            onClick={() => analisar.mutate()}
            disabled={analisar.isPending}
          >
            {analisar.isPending ? TEXTOS.apoioAoComite.analisando : parecerDefasado ? TEXTOS.apoioAoComite.pedirNovaAnalise : TEXTOS.apoioAoComite.botao}
          </Botao>
        )}
      </header>

      {analisar.isError && <AvisoDeErro erro={analisar.error} titulo={TEXTOS.apoioAoComite.falhou} />}
      {pareceres.isPending && <Esqueleto linhas={2} />}
      {iaDisponivel && pareceres.data && !parecer && (
        <p className="text-sm text-slate-500">{TEXTOS.apoioAoComite.semParecer}</p>
      )}
      {/* O comitê só vê o parecer mais recente — se chegou código depois dele, dizer isso é a
          diferença entre apoiar a decisão e induzi-la ao erro. */}
      {parecer && parecerDefasado && (
        <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-slate-800">
          {TEXTOS.apoioAoComite.parecerDefasado}
        </p>
      )}
      {parecer && <Parecer parecer={parecer} />}

      <RevisoesDeCodigo
        revisoes={revisoes.data ?? []}
        carregando={revisoes.isPending}
        indisponibilidade={
          situacaoDasRevisoes.data?.disponivel === false ? situacaoDasRevisoes.data.motivoIndisponivel : null
        }
      />

      {/* O que de fato entrou na branch principal citando esta solicitação — a evidência que a
          revisão de PR não dá, porque PR é proposta e commit na principal é integração. */}
      <CommitsDaGmud gmudId={gmudId} status={status} />
    </section>
  )
}

function Parecer({ parecer }: { parecer: ParecerDeGmud }) {
  if (parecer.situacao === 'FALHOU') {
    return (
      <p className="text-sm text-slate-600">
        {TEXTOS.apoioAoComite.falhou}
        {parecer.erro ? `: ${parecer.erro}` : '.'}
      </p>
    )
  }
  return (
    <div className="space-y-3">
      {parecer.recomendacao && <Recomendacao valor={parecer.recomendacao} />}
      {parecer.resumo && <p className="text-sm leading-relaxed text-slate-700">{parecer.resumo}</p>}

      {parecer.ressalvas.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-slate-700">{TEXTOS.apoioAoComite.ressalvas}</h3>
          <ul className="mt-2 space-y-2">
            {porSeveridade(parecer.ressalvas).map((ressalva, indice) => (
              <li key={`${ressalva.codigo}-${indice}`} className="rounded-md border border-slate-200 bg-slate-50 p-3">
                <p className="flex flex-wrap items-center gap-2 text-sm font-medium text-slate-800">
                  <SeveridadeDaIa severidade={ressalva.severidade} />
                  {ressalva.titulo ?? ressalva.codigo}
                </p>
                {ressalva.explicacao && <p className="mt-1 text-sm text-slate-600">{ressalva.explicacao}</p>}
                {ressalva.verificacao && (
                  <p className="mt-1 text-sm text-slate-500">
                    <span className="font-medium">{TEXTOS.apoioAoComite.comoConferir}</span> {ressalva.verificacao}
                  </p>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* A mesma procedência do chamado e do fechamento, do mesmo componente: a ressalva sobre o
          que um texto de máquina é não pode variar de tela para tela. */}
      <SeloDeMaquina modelo={parecer.modelo} quando={parecer.concluidoEm} />
    </div>
  )
}

/** A recomendação em destaque, nas cores de semáforo que o público já lê sem legenda. */
function Recomendacao({ valor }: { valor: RecomendacaoDaIa }) {
  const classes: Record<RecomendacaoDaIa, string> = {
    RECOMENDA_APROVAR: 'bg-green-100 text-green-900',
    APROVAR_COM_RESSALVAS: 'bg-amber-100 text-amber-900',
    RECOMENDA_REPROVAR: 'bg-red-100 text-red-900',
  }
  return (
    <span className={`inline-flex rounded-full px-3 py-1 text-sm font-semibold ${classes[valor]}`}>
      {TEXTOS.apoioAoComite.recomendacao[valor]}
    </span>
  )
}

function doComite(usuario: UsuarioLogado | null): boolean {
  return usuario?.papeis.some((papel) => PAPEIS_DO_COMITE.includes(papel)) ?? false
}
